import Axios from 'axios';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import { Store } from '../Store';
import { getError } from '../utils';
import { api } from '../Api';
import '../assets/profile.css';

/**
 * MY PROFILE — the member's own details, in one place.
 *
 * Served on both /profile and /settings (SettingScreen re-exports this), so
 * there is one edit form and one set of rules on the web.
 *
 * What may be changed is decided by the server (GET /getsetting ships the
 * profileEdit status for every field): a blank field may be filled in once, a
 * saved one is read-only here with the reason shown. Only fields that actually
 * changed are sent, and the save asks for the current password.
 */

// The picture every account starts with: it means "no photo set yet".
const DEFAULT_PROFILE_PHOTO =
  'https://res.cloudinary.com/dddogyqqa/image/upload/v1670435815/logo/clickcart-logo_wfktlg.png';

const MOBILE_BANKS = ['bKash', 'Nagad', 'Rocket', 'Upay'];

const SECTIONS = [
  {
    key: 'personal',
    title: 'Personal',
    icon: 'fa-user',
    note: 'Your name and identity as they appear on your NID.',
    fields: [
      { key: 'name', label: 'Full name', placeholder: 'As written on your NID', autoComplete: 'name' },
      { key: 'gender', label: 'Gender', type: 'select',
        options: [['male', 'Male'], ['female', 'Female'], ['other', 'Other']] },
      { key: 'dob', label: 'Date of birth', type: 'date' },
      { key: 'nid', label: 'NID / Birth certificate number', inputMode: 'numeric',
        placeholder: '10, 13 or 17 digits', help: 'Digits only, no spaces.' },
    ],
  },
  {
    key: 'contact',
    title: 'Contact',
    icon: 'fa-address-book',
    note: 'How we reach you about orders and withdrawals.',
    fields: [
      { key: 'phone', label: 'Mobile number', type: 'tel', inputMode: 'tel',
        placeholder: '01XXXXXXXXX', autoComplete: 'tel' },
      { key: 'email', label: 'Email address', type: 'email', placeholder: 'you@example.com',
        autoComplete: 'email' },
      { key: 'fblink', label: 'Facebook profile link', type: 'url',
        placeholder: 'https://facebook.com/yourname' },
    ],
  },
  {
    key: 'address',
    title: 'Address',
    icon: 'fa-location-dot',
    note: 'Where you live now.',
    fields: [
      { key: 'address', label: 'Present address', type: 'textarea', wide: true,
        placeholder: 'House, road, area, thana, district', autoComplete: 'street-address' },
    ],
  },
  {
    key: 'family',
    title: 'Family contacts',
    icon: 'fa-people-roof',
    note: 'Used only if we cannot reach you.',
    fields: [
      { key: 'fatherphone', label: "Father's mobile", type: 'tel', inputMode: 'tel', placeholder: '01XXXXXXXXX' },
      { key: 'motherphone', label: "Mother's mobile", type: 'tel', inputMode: 'tel', placeholder: '01XXXXXXXXX' },
      { key: 'spousephone', label: "Spouse's mobile", type: 'tel', inputMode: 'tel', placeholder: '01XXXXXXXXX' },
    ],
  },
  {
    key: 'mobilebank',
    title: 'Mobile banking',
    icon: 'fa-mobile-screen-button',
    note: 'Withdrawals are sent here. Check every digit.',
    fields: [
      { key: 'mobilebankname', label: 'Service', type: 'select',
        options: MOBILE_BANKS.map((b) => [b, b]) },
      { key: 'mobilebankaccount', label: 'Account number', type: 'tel', inputMode: 'numeric',
        placeholder: '01XXXXXXXXX', help: 'The number registered with the service.' },
    ],
  },
  {
    key: 'bank',
    title: 'Bank account',
    icon: 'fa-building-columns',
    note: 'Optional. Used for bank transfers.',
    fields: [
      { key: 'bankname', label: 'Bank name', placeholder: 'e.g. Dutch-Bangla Bank' },
      { key: 'bankbranch', label: 'Branch', placeholder: 'e.g. Motijheel' },
      { key: 'bankaccount', label: 'Account number', inputMode: 'numeric', wide: true,
        placeholder: 'Account number' },
    ],
  },
  {
    key: 'nominee',
    title: 'Nominee',
    icon: 'fa-hand-holding-heart',
    note: 'The person who receives your balance if anything happens to you.',
    fields: [
      { key: 'benificiaryname', label: 'Nominee name' },
      { key: 'benificiaryrelation', label: 'Relationship', placeholder: 'e.g. Wife, Son, Mother' },
      { key: 'benificiarynid', label: 'Nominee NID', inputMode: 'numeric', placeholder: '10, 13 or 17 digits' },
    ],
  },
];

const FORM_FIELDS = SECTIONS.flatMap((s) => s.fields);
const LABELS = Object.fromEntries(FORM_FIELDS.map((f) => [f.key, f.label]));

/* ------------------------------------------------------------ helpers */

const str = (v) => (v === null || v === undefined ? '' : String(v)).trim();

/** Any saved date of birth -> YYYY-MM-DD, the only format a date input shows. */
const toIsoDob = (raw) => {
  const v = str(raw);
  if (!v) return '';
  let y; let m; let d;
  let hit = v.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s].*)?$/);
  if (hit) [, y, m, d] = hit;
  else {
    hit = v.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
    if (hit) [, d, m, y] = hit;
  }
  if (!hit) return '';
  const dt = new Date(Date.UTC(+y, +m - 1, +d));
  if (dt.getUTCFullYear() !== +y || dt.getUTCMonth() !== +m - 1 || dt.getUTCDate() !== +d) return '';
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
};

/** The saved value as the input should show it. */
const toFormValue = (key, raw) => {
  const v = str(raw);
  if (v === 'null' || v === 'undefined') return '';
  if (key === 'dob') return toIsoDob(v) || v;
  if (key === 'gender') return v.toLowerCase();
  if (key === 'mobilebankname') return MOBILE_BANKS.find((b) => b.toLowerCase() === v.toLowerCase()) || v;
  return v;
};

const cleanPhone = (raw) => {
  let d = str(raw).replace(/[\s\-().]/g, '');
  if (d.startsWith('+')) d = d.slice(1);
  if (d.startsWith('88')) d = d.slice(2);
  return d;
};

/** Mirrors validateMemberValue in backend/services/profileEditService.js. */
const validate = (key, raw) => {
  const v = str(raw);
  if (!v) return 'This cannot be left empty.';
  if (['phone', 'fatherphone', 'motherphone', 'spousephone'].includes(key)) {
    return /^01[3-9]\d{8}$/.test(cleanPhone(v)) ? '' : 'Enter a valid mobile number, like 01712345678.';
  }
  if (key === 'mobilebankaccount') {
    return /^01[3-9]\d{8}\d?$/.test(cleanPhone(v)) ? '' : 'Enter a valid mobile number, like 01712345678.';
  }
  if (key === 'email') {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v) ? '' : 'Enter a valid email address.';
  }
  if (key === 'nid' || key === 'benificiarynid') {
    return /^(\d{10}|\d{13}|\d{17})$/.test(v.replace(/\s/g, ''))
      ? '' : 'Enter a 10 or 13 digit NID, or a 17 digit birth certificate number.';
  }
  if (key === 'dob') {
    const iso = toIsoDob(v);
    const today = new Date().toISOString().slice(0, 10);
    return iso && +iso.slice(0, 4) >= 1900 && iso <= today ? '' : 'Enter a real date of birth.';
  }
  if (key === 'fblink') {
    return /^(https?:\/\/)?([a-z0-9-]+\.)*(facebook\.com|fb\.com|fb\.me)(\/\S*)?$/i.test(v)
      ? '' : 'Enter your Facebook profile link, like https://facebook.com/yourname.';
  }
  if (key === 'name' && v.length < 2) return 'Enter your full name.';
  if (key === 'bankaccount' && !/^[0-9\s-]{6,30}$/.test(v)) return 'Use digits only.';
  if (/[<>]/.test(v)) return 'Remove the < and > characters.';
  return '';
};

const fmtDate = (d) => {
  const t = d ? new Date(d) : null;
  if (!t || Number.isNaN(t.getTime())) return '';
  return t.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const fmtNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString('en-US', { maximumFractionDigits: 2 }) : '0';
};

/** The line under a field, from the server's lock code. */
const lockText = (status) => {
  if (!status) return '';
  if (status.canEdit) return status.unlimited ? '' : 'You can fill this in once.';
  if (status.lockCode === 'system') return 'Set by the office.';
  if (status.lockCode === 'off') return 'Editing is switched off for this field.';
  return status.lockReason || 'Locked. Contact support to change it.';
};

/* ---------------------------------------------------------- component */

export default function ProfileScreen() {
  const { state, dispatch: ctxDispatch } = useContext(Store);
  const { userInfo } = state;
  const navigate = useNavigate();
  const token = userInfo?.token;
  const auth = useMemo(() => ({ headers: { authorization: `Bearer ${token}` } }), [token]);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [profile, setProfile] = useState({});
  const [status, setStatus] = useState({});
  const [initial, setInitial] = useState({});
  const [form, setForm] = useState({});
  const [touched, setTouched] = useState({});
  const [serverErrors, setServerErrors] = useState({});
  const [stats, setStats] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [savedNote, setSavedNote] = useState('');

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState('');

  const applyProfile = useCallback((data) => {
    setProfile(data || {});
    setStatus(Object.fromEntries((data?.profileEdit || []).map((f) => [f.key, f])));
    const values = Object.fromEntries(FORM_FIELDS.map((f) => [f.key, toFormValue(f.key, data?.[f.key])]));
    setInitial(values);
    setForm(values);
    setTouched({});
    setServerErrors({});
  }, []);

  const loadLastUpdated = useCallback(async () => {
    try {
      const { data } = await Axios.get(api + '/api/users/profile-edit/history?page=1&pageSize=1', auth);
      setLastUpdated(data?.rows?.[0]?.createdAt || null);
    } catch {
      // Informational only.
    }
  }, [auth]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const { data } = await Axios.get(api + '/api/users/getsetting', auth);
      applyProfile(data);
    } catch (err) {
      setLoadError(getError(err));
    } finally {
      setLoading(false);
    }
    loadLastUpdated();
    try {
      const { data } = await Axios.get(api + `/api/users/point/${userInfo._id}`, auth);
      setStats(data || null);
    } catch {
      setStats(null);
    }
  }, [auth, applyProfile, loadLastUpdated, userInfo._id]);

  useEffect(() => {
    load();
  }, [load]);

  const isLocked = (key) => {
    const s = status[key];
    return s ? !s.canEdit : false;
  };

  const dirtyKeys = FORM_FIELDS.map((f) => f.key).filter(
    (k) => !isLocked(k) && str(form[k]) !== str(initial[k])
  );
  const dirty = dirtyKeys.length > 0;
  const errors = Object.fromEntries(
    dirtyKeys.map((k) => [k, validate(k, form[k])]).filter(([, e]) => e)
  );
  const hasErrors = Object.keys(errors).length > 0;

  // Leaving the page with unsaved edits asks first.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const setField = (key) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [key]: value }));
    setServerErrors((s) => ({ ...s, [key]: undefined }));
    setSavedNote('');
  };

  const discard = () => {
    setForm(initial);
    setTouched({});
    setServerErrors({});
  };

  const goBack = () => {
    if (dirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
    navigate('/user-panel');
  };

  const askToSave = () => {
    if (!dirty || saving) return;
    setTouched(Object.fromEntries(dirtyKeys.map((k) => [k, true])));
    if (hasErrors) {
      toast.error('Please fix the highlighted fields.');
      const first = document.getElementById(`pf-${Object.keys(errors)[0]}`);
      if (first) first.focus();
      return;
    }
    setPasswordError('');
    setCurrentPassword('');
    setConfirmOpen(true);
  };

  /** Keep the header, the menu and localStorage in step with what was saved. */
  const refreshUserInfo = (patch) => {
    const merged = { ...userInfo, ...patch };
    ctxDispatch({ type: 'USER_SIGNIN', payload: merged });
    localStorage.setItem('userInfo', JSON.stringify(merged));
  };

  const save = async (e) => {
    if (e) e.preventDefault();
    if (!currentPassword) {
      setPasswordError('Enter your current password.');
      return;
    }
    const payload = Object.fromEntries(dirtyKeys.map((k) => [k, str(form[k])]));
    setSaving(true);
    try {
      const { data } = await Axios.put(
        api + '/api/users/savesetting',
        { ...payload, currentPassword },
        auth
      );
      setConfirmOpen(false);
      setCurrentPassword('');
      const changed = (data?.changed || []).map((c) => c.label);
      const note = changed.length ? `Saved: ${changed.join(', ')}.` : 'Nothing needed saving.';
      toast.success(changed.length ? 'Your profile has been saved.' : 'Nothing has changed.');
      setSavedNote(note);
      const patch = {};
      ['name', 'phone', 'email'].forEach((k) => {
        const row = (data?.changed || []).find((c) => c.field === k);
        if (row) patch[k] = row.to;
      });
      if (Object.keys(patch).length) refreshUserInfo(patch);
      // Read back what the server stored, so the form shows exactly that.
      const fresh = await Axios.get(api + '/api/users/getsetting', auth);
      applyProfile(fresh.data);
      loadLastUpdated();
    } catch (err) {
      const body = err?.response?.data || {};
      if (body.field === 'currentPassword') {
        setPasswordError(body.message);
      } else {
        setConfirmOpen(false);
        if (body.errors && Object.keys(body.errors).length) setServerErrors(body.errors);
        if (Array.isArray(body.blocked) && body.blocked.length) {
          setServerErrors((s) => ({
            ...s,
            ...Object.fromEntries(body.blocked.map((b) => [b.key, b.reason])),
          }));
          // The lock state changed under us; read it again.
          Axios.get(api + '/api/users/getsetting', auth)
            .then(({ data }) => setStatus(Object.fromEntries((data?.profileEdit || []).map((f) => [f.key, f]))))
            .catch(() => {});
        }
        toast.error(getError(err));
      }
    } finally {
      setSaving(false);
    }
  };

  /* -------------------------------------------- photo and signature */

  const photoOnFile = str(profile.profilePhoto);
  const hasPhoto = photoOnFile !== '' && photoOnFile !== DEFAULT_PROFILE_PHOTO;
  const canPhoto = status.profilePhoto ? status.profilePhoto.canEdit : !hasPhoto;
  const canSign = status.sign ? status.sign.canEdit : !str(profile.sign);

  const upload = async (kind, file) => {
    if (!file) return;
    const maxKb = kind === 'sign' ? 150 : 250;
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Please choose a JPG or PNG image.');
      return;
    }
    if (file.size > maxKb * 1024) {
      toast.error(`The image must be ${maxKb} KB or smaller.`);
      return;
    }
    const body = new FormData();
    body.append('file', file);
    setUploading(kind);
    try {
      const { data } = await Axios.post(api + '/api/upload/userphoto', body, {
        headers: { ...auth.headers, 'Content-Type': 'multipart/form-data' },
      });
      await Axios.put(api + '/api/users/savephoto', { value: kind, link: data.secure_url }, auth);
      setProfile((p) => ({ ...p, [kind]: data.secure_url }));
      if (kind === 'profilePhoto') refreshUserInfo({ profilePhoto: data.secure_url });
      toast.success(kind === 'sign' ? 'Signature saved.' : 'Profile photo saved.');
      const fresh = await Axios.get(api + '/api/users/getsetting', auth);
      setStatus(Object.fromEntries((fresh.data?.profileEdit || []).map((f) => [f.key, f])));
      setProfile((p) => ({ ...p, ...fresh.data }));
      loadLastUpdated();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setUploading('');
    }
  };

  /* ------------------------------------------------------- render */

  const renderField = (field) => {
    const locked = isLocked(field.key);
    const value = form[field.key] ?? '';
    const error = serverErrors[field.key] || (touched[field.key] && errors[field.key]) || '';
    const changed = !locked && str(value) !== str(initial[field.key]);
    const hint = locked ? lockText(status[field.key]) : field.help || lockText(status[field.key]);
    const id = `pf-${field.key}`;
    const common = {
      id,
      name: field.key,
      value,
      onChange: setField(field.key),
      onBlur: () => setTouched((t) => ({ ...t, [field.key]: true })),
      disabled: locked || loading,
      className: `pf-input${error ? ' is-invalid' : ''}`,
      'aria-invalid': error ? 'true' : 'false',
      'aria-describedby': `${id}-help`,
    };

    let control;
    if (field.type === 'select') {
      const known = field.options.some(([v]) => v === value);
      control = (
        <select {...common}>
          <option value="">Choose…</option>
          {!known && value ? <option value={value}>{value}</option> : null}
          {field.options.map(([v, label]) => (
            <option key={v} value={v}>{label}</option>
          ))}
        </select>
      );
    } else if (field.type === 'textarea') {
      control = <textarea rows={3} placeholder={field.placeholder} autoComplete={field.autoComplete} {...common} />;
    } else {
      control = (
        <input
          type={field.type === 'date' ? 'date' : field.type || 'text'}
          inputMode={field.inputMode}
          placeholder={field.placeholder}
          autoComplete={field.autoComplete}
          max={field.type === 'date' ? new Date().toISOString().slice(0, 10) : undefined}
          min={field.type === 'date' ? '1900-01-01' : undefined}
          {...common}
        />
      );
    }

    return (
      <div key={field.key} className={`pf-field${field.wide ? ' is-wide' : ''}${locked ? ' is-locked' : ''}`}>
        <label htmlFor={id} className="pf-label">
          {field.label}
          {locked ? <i className="fa-solid fa-lock pf-lock" title="Locked" aria-label="Locked" /> : null}
          {changed ? <span className="pf-changed">Edited</span> : null}
        </label>
        {control}
        <div id={`${id}-help`} className={error ? 'pf-error' : 'pf-help'}>
          {error || hint || ' '}
        </div>
      </div>
    );
  };

  const photoSrc = hasPhoto ? photoOnFile : '';
  const initials = str(profile.name || userInfo.name)
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

  return (
    <div className="pf-page">
      <Helmet>
        <title>My Profile | ClickCart International Ltd.</title>
      </Helmet>
      <div className="pf-wrap">
        <div className="pf-top">
          <button type="button" className="pf-iconbtn" onClick={goBack} aria-label="Back">
            <i className="fa-solid fa-arrow-left" />
          </button>
          <div className="pf-top-title">
            <h1>My Profile</h1>
            <span>
              {lastUpdated ? `Last updated ${fmtDate(lastUpdated)}` : 'Keep your details up to date'}
            </span>
          </div>
          <button
            type="button"
            className={`pf-iconbtn${loading ? ' is-spinning' : ''}`}
            onClick={() => (!dirty || window.confirm('Reload and lose your unsaved changes?')) && load()}
            aria-label="Reload"
          >
            <i className="fa-solid fa-rotate-right" />
          </button>
        </div>

        {loadError ? (
          <div className="pf-alert is-error">
            {loadError} <button type="button" onClick={load}>Try again</button>
          </div>
        ) : null}

        <section className="pf-hero">
          <div className="pf-hero-glow" />
          <div className="pf-avatar-wrap">
            {photoSrc ? (
              <img className="pf-avatar" src={photoSrc} alt="" />
            ) : (
              <div className="pf-avatar pf-avatar-empty">{initials || <i className="fa-solid fa-user" />}</div>
            )}
            {canPhoto ? (
              <label className="pf-avatar-btn" title="Upload profile photo">
                {uploading === 'profilePhoto' ? <Spinner animation="border" size="sm" /> : <i className="fa-solid fa-camera" />}
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  hidden
                  disabled={Boolean(uploading)}
                  onChange={(e) => { upload('profilePhoto', e.target.files[0]); e.target.value = ''; }}
                />
              </label>
            ) : (
              <span className="pf-avatar-btn is-locked" title={lockText(status.profilePhoto) || 'Locked'}>
                <i className="fa-solid fa-lock" />
              </span>
            )}
          </div>
          <div className="pf-hero-id">
            <h2>{profile.name || userInfo.name}</h2>
            <div className="pf-hero-sub">@{profile.username || userInfo.username}</div>
            <div className="pf-chips">
              {profile.reference ? <span className="pf-chip">Ref: {profile.reference}</span> : null}
              {profile.placement ? <span className="pf-chip">Placement: {profile.placement}</span> : null}
              {profile.position ? <span className="pf-chip">Side: {profile.position}</span> : null}
            </div>
            {canPhoto ? (
              <div className="pf-hero-hint">Tap the camera to add a photo (JPG or PNG, up to 250 KB).</div>
            ) : null}
          </div>
          {stats ? (
            <div className="pf-stats">
              <div><small>Points</small><strong>{fmtNum(stats.point > 499 ? stats.point - 500 : 0)}</strong></div>
              <div><small>Available</small><strong>{fmtNum(stats.availablebalance)}</strong></div>
              <div><small>Team left</small><strong>{fmtNum(stats.totalLeft)}</strong></div>
              <div><small>Team right</small><strong>{fmtNum(stats.totalRight)}</strong></div>
            </div>
          ) : null}
        </section>

        <div className="pf-alert is-info">
          <i className="fa-solid fa-shield-halved" />
          <span>
            You can fill in any blank field once. After it is saved it is locked
            <i className="fa-solid fa-lock pf-lock" /> and only the office can change it, so check
            each value before saving. Saving asks for your password.
          </span>
        </div>

        {savedNote ? (
          <div className="pf-alert is-success" role="status">
            <i className="fa-solid fa-circle-check" /> <span>{savedNote}</span>
          </div>
        ) : null}

        <form
          onSubmit={(e) => { e.preventDefault(); askToSave(); }}
          noValidate
          className={loading ? 'is-loading' : ''}
        >
          <div className="pf-grid">
            {SECTIONS.map((section) => (
              <section key={section.key} className={`pf-card${section.fields.length < 2 ? ' is-full' : ''}`}>
                <header className="pf-card-head">
                  <span className="pf-card-ico"><i className={`fa-solid ${section.icon}`} /></span>
                  <div>
                    <h3>{section.title}</h3>
                    <p>{section.note}</p>
                  </div>
                </header>
                <div className="pf-fields">{section.fields.map(renderField)}</div>
              </section>
            ))}

            <section className="pf-card">
              <header className="pf-card-head">
                <span className="pf-card-ico"><i className="fa-solid fa-signature" /></span>
                <div>
                  <h3>Signature</h3>
                  <p>Sign on white paper and upload a photo (JPG or PNG, up to 150 KB).</p>
                </div>
              </header>
              <div className="pf-sign">
                {str(profile.sign) ? (
                  <img src={profile.sign} alt="Your signature" />
                ) : (
                  <div className="pf-sign-empty">No signature yet</div>
                )}
                {canSign ? (
                  <label className="pf-btn pf-btn-ghost">
                    {uploading === 'sign' ? <Spinner animation="border" size="sm" /> : <i className="fa-solid fa-upload" />}
                    {' '}Upload signature
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      hidden
                      disabled={Boolean(uploading)}
                      onChange={(e) => { upload('sign', e.target.files[0]); e.target.value = ''; }}
                    />
                  </label>
                ) : (
                  <div className="pf-help"><i className="fa-solid fa-lock pf-lock" /> {lockText(status.sign) || 'Locked.'}</div>
                )}
              </div>
            </section>

            <section className="pf-card">
              <header className="pf-card-head">
                <span className="pf-card-ico"><i className="fa-solid fa-id-badge" /></span>
                <div>
                  <h3>Account</h3>
                  <p>Set when your account was opened.</p>
                </div>
              </header>
              <dl className="pf-readonly">
                <div><dt>Username</dt><dd>{profile.username || userInfo.username}</dd></div>
                <div><dt>Reference</dt><dd>{str(profile.reference) || '—'}</dd></div>
                <div><dt>Placement</dt><dd>{str(profile.placement) || '—'}</dd></div>
                <div><dt>Joined</dt><dd>{fmtDate(profile.createdAt) || '—'}</dd></div>
              </dl>
            </section>
          </div>

          <div className={`pf-savebar${dirty ? ' is-dirty' : ''}`}>
            <div className="pf-savebar-text">
              {dirty ? (
                <>
                  <i className="fa-solid fa-circle-exclamation" />{' '}
                  {dirtyKeys.length === 1 ? '1 unsaved change' : `${dirtyKeys.length} unsaved changes`}
                  <span className="pf-savebar-list">: {dirtyKeys.map((k) => LABELS[k]).join(', ')}</span>
                </>
              ) : (
                <>
                  <i className="fa-solid fa-circle-check" /> All changes saved
                </>
              )}
            </div>
            <div className="pf-savebar-actions">
              <button type="button" className="pf-btn pf-btn-ghost" onClick={discard} disabled={!dirty || saving}>
                Discard
              </button>
              <button type="submit" className="pf-btn pf-btn-primary" disabled={!dirty || saving || loading}>
                {saving ? <Spinner animation="border" size="sm" /> : <i className="fa-solid fa-floppy-disk" />}
                {' '}Save changes
              </button>
            </div>
          </div>
        </form>

        <ChangePasswordCard auth={auth} />
      </div>

      <Modal show={confirmOpen} onHide={() => !saving && setConfirmOpen(false)} centered>
        <form onSubmit={save}>
          <Modal.Header closeButton={!saving}>
            <Modal.Title as="h5">Confirm with your password</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="pf-modal-text">You are saving:</p>
            <ul className="pf-modal-list">
              {dirtyKeys.map((k) => (
                <li key={k}>
                  <span>{LABELS[k]}</span>
                  <b>{str(form[k])}</b>
                </li>
              ))}
            </ul>
            <p className="pf-modal-warn">
              <i className="fa-solid fa-lock" /> Once saved, these fields are locked.
            </p>
            <label htmlFor="pf-current-password" className="pf-label">Current password</label>
            <PasswordInput
              id="pf-current-password"
              value={currentPassword}
              onChange={(e) => { setCurrentPassword(e.target.value); setPasswordError(''); }}
              autoComplete="current-password"
              autoFocus
              invalid={Boolean(passwordError)}
            />
            <div className={passwordError ? 'pf-error' : 'pf-help'}>
              {passwordError || 'The password you sign in with.'}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="pf-btn pf-btn-ghost" onClick={() => setConfirmOpen(false)} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="pf-btn pf-btn-primary" disabled={saving || !currentPassword}>
              {saving ? <Spinner animation="border" size="sm" /> : null} Save
            </button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
}

/* ------------------------------------------------------------ password */

function PasswordInput({ invalid, ...props }) {
  const [show, setShow] = useState(false);
  return (
    <div className="pf-pass">
      <input type={show ? 'text' : 'password'} className={`pf-input${invalid ? ' is-invalid' : ''}`} {...props} />
      <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? 'Hide password' : 'Show password'}>
        <i className={`fa-solid ${show ? 'fa-eye-slash' : 'fa-eye'}`} />
      </button>
    </div>
  );
}

const strengthOf = (p) => {
  if (!p) return { score: 0, label: '' };
  let score = 0;
  if (p.length >= 6) score += 1;
  if (p.length >= 10) score += 1;
  if (/[A-Z]/.test(p) && /[a-z]/.test(p)) score += 1;
  if (/\d/.test(p) && /[^A-Za-z0-9]/.test(p)) score += 1;
  return { score, label: ['Too short', 'Weak', 'Fair', 'Good', 'Strong'][score] };
};

function ChangePasswordCard({ auth }) {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState({});

  const strength = strengthOf(newPassword);

  const submit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!oldPassword) next.oldPassword = 'Enter your current password.';
    if (newPassword.length < 6) next.newPassword = 'Use at least 6 characters.';
    else if (newPassword === oldPassword) next.newPassword = 'Choose a password different from your current one.';
    if (confirm !== newPassword) next.confirm = 'The two new passwords do not match.';
    setError(next);
    if (Object.keys(next).length) return;
    setBusy(true);
    try {
      await Axios.put(api + '/api/users/changepassword', { oldPassword, newPassword }, auth);
      toast.success('Password changed.');
      setOldPassword('');
      setNewPassword('');
      setConfirm('');
    } catch (err) {
      const body = err?.response?.data || {};
      if (body.field === 'oldPassword') setError({ oldPassword: body.message });
      else toast.error(getError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="pf-card pf-card-solo">
      <header className="pf-card-head">
        <span className="pf-card-ico"><i className="fa-solid fa-key" /></span>
        <div>
          <h3>Change password</h3>
          <p>You need your password to sign in and to withdraw.</p>
        </div>
      </header>
      <form onSubmit={submit} noValidate className="pf-fields">
        <div className="pf-field is-wide">
          <label htmlFor="pf-old-password" className="pf-label">Current password</label>
          <PasswordInput id="pf-old-password" value={oldPassword} autoComplete="current-password"
            invalid={Boolean(error.oldPassword)}
            onChange={(e) => { setOldPassword(e.target.value); setError((x) => ({ ...x, oldPassword: '' })); }} />
          <div className={error.oldPassword ? 'pf-error' : 'pf-help'}>{error.oldPassword || ' '}</div>
        </div>
        <div className="pf-field">
          <label htmlFor="pf-new-password" className="pf-label">New password</label>
          <PasswordInput id="pf-new-password" value={newPassword} autoComplete="new-password"
            invalid={Boolean(error.newPassword)}
            onChange={(e) => { setNewPassword(e.target.value); setError((x) => ({ ...x, newPassword: '' })); }} />
          {newPassword ? (
            <div className="pf-strength" data-score={strength.score}><span /><em>{strength.label}</em></div>
          ) : null}
          <div className={error.newPassword ? 'pf-error' : 'pf-help'}>
            {error.newPassword || 'At least 6 characters. Mix letters and numbers.'}
          </div>
        </div>
        <div className="pf-field">
          <label htmlFor="pf-confirm-password" className="pf-label">Type the new password again</label>
          <PasswordInput id="pf-confirm-password" value={confirm} autoComplete="new-password"
            invalid={Boolean(error.confirm)}
            onChange={(e) => { setConfirm(e.target.value); setError((x) => ({ ...x, confirm: '' })); }} />
          <div className={error.confirm ? 'pf-error' : 'pf-help'}>{error.confirm || ' '}</div>
        </div>
        <div className="pf-field is-wide">
          <button type="submit" className="pf-btn pf-btn-primary pf-btn-block"
            disabled={busy || !oldPassword || !newPassword || !confirm}>
            {busy ? <Spinner animation="border" size="sm" /> : <i className="fa-solid fa-shield-halved" />}
            {' '}Change password
          </button>
        </div>
      </form>
    </section>
  );
}
